import {
  getDatabase,
  ref,
  onValue,
  set,
  remove,
  update,
  child,
  get,
} from "firebase/database";
import { getAuth } from "firebase/auth";

const db = getDatabase();
const auth = getAuth();
const torneioRef = ref(db, "torneio/super"); // caminho fixo

export const listenTorneio = (callback: (data: any) => void) => {
  return onValue(torneioRef, (snapshot) => {
    callback(snapshot.val());
  });
};

export const criarTorneio = async (
  tipo: string,
  inscritos: Record<string, { nome: string }> = {}
) => {
  await set(torneioRef, {
    criadoEm: Date.now(),
    tipo,
    maxVagas: tipo === "super12" ? 12 : 8,
    inscritos,
  });
};

export const atualizarInscritosTorneio = async (
  inscritos: Record<string, { nome: string }>
) => {
  await update(torneioRef, {
    inscritos,
  });
};

export const deletarTorneio = async () => {
  try {
    await remove(torneioRef);
  } catch (error) {
    console.error("Erro ao deletar o torneio:", error);
    throw error;
  }
};

export const entrarNoTorneio = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("Você precisa estar logado.");

  const snapshot = await new Promise<any>((resolve, reject) => {
    onValue(
      torneioRef,
      (snap) => resolve(snap.val()),
      (err) => reject(err),
      { onlyOnce: true }
    );
  });

  if (!snapshot) throw new Error("Nenhum torneio ativo.");

  const inscritos = snapshot.inscritos || {};
  const maxVagas = snapshot.maxVagas || 12;

  if (Object.keys(inscritos).length >= maxVagas) {
    throw new Error("Torneio lotado.");
  }

  if (inscritos[user.uid]) {
    throw new Error("Você já está inscrito.");
  }

  const userRef = ref(db, `torneio/super/inscritos/${user.uid}`);
  // Busca o nome atualizado do banco
  const userDbRef = ref(db, `users/${user.uid}/displayName`);
  const fullName = await new Promise<string>((resolve, reject) => {
    onValue(
      userDbRef,
      (snap) => resolve(snap.val() || "Jogadora"),
      (err) => reject(err),
      { onlyOnce: true }
    );
  });

  await set(userRef, {
    nome: fullName,
  });

  return snapshot.tipo;
};

// Função para embaralhar array (algoritmo Fisher-Yates)
function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

interface RoundMatch {
  dupla1: [string, string];
  dupla2: [string, string];
}

type CountMap = Record<string, Record<string, number>>;

function createCountMap(jogadores: string[]): CountMap {
  const map: CountMap = {};
  jogadores.forEach((a) => {
    map[a] = {};
    jogadores.forEach((b) => {
      if (a !== b) {
        map[a][b] = 0;
      }
    });
  });
  return map;
}

function addPairCount(map: CountMap, a: string, b: string) {
  map[a][b] += 1;
  map[b][a] += 1;
}

function getPairCount(map: CountMap, a: string, b: string) {
  return map[a]?.[b] ?? 0;
}

function registerRound(
  partidas: RoundMatch[],
  teammateCounts: CountMap,
  opponentCounts: CountMap
) {
  partidas.forEach(({ dupla1, dupla2 }) => {
    addPairCount(teammateCounts, dupla1[0], dupla1[1]);
    addPairCount(teammateCounts, dupla2[0], dupla2[1]);

    dupla1.forEach((j1) => {
      dupla2.forEach((j2) => {
        addPairCount(opponentCounts, j1, j2);
      });
    });
  });
}

function buildLastRoundMaps(
  jogadores: string[],
  partidas: RoundMatch[]
): { partners: CountMap; opponents: CountMap } {
  const partners = createCountMap(jogadores);
  const opponents = createCountMap(jogadores);
  registerRound(partidas, partners, opponents);
  return { partners, opponents };
}

function computeRoundScore(
  partidas: RoundMatch[],
  teammateCounts: CountMap,
  opponentCounts: CountMap,
  lastRoundPartners: CountMap,
  lastRoundOpponents: CountMap
) {
  let score = 0;

  partidas.forEach(({ dupla1, dupla2 }) => {
    const [a1, a2] = dupla1;
    const [b1, b2] = dupla2;

    const repeatedTeammateA = getPairCount(teammateCounts, a1, a2);
    const repeatedTeammateB = getPairCount(teammateCounts, b1, b2);

    score += repeatedTeammateA === 0 ? 0 : 250 + repeatedTeammateA * 350;
    score += repeatedTeammateB === 0 ? 0 : 250 + repeatedTeammateB * 350;

    if (getPairCount(lastRoundPartners, a1, a2) > 0) score += 900;
    if (getPairCount(lastRoundPartners, b1, b2) > 0) score += 900;

    const confrontos: Array<[string, string]> = [
      [a1, b1],
      [a1, b2],
      [a2, b1],
      [a2, b2],
    ];

    confrontos.forEach(([j1, j2]) => {
      const repeatedOpponent = getPairCount(opponentCounts, j1, j2);
      score += repeatedOpponent * 24;

      if (getPairCount(lastRoundOpponents, j1, j2) > 0) {
        score += 80;
      }
    });
  });

  return score;
}

function generateCandidateRound(jogadores: string[]): RoundMatch[] {
  const embaralhados = shuffleArray(jogadores);
  const duplas: Array<[string, string]> = [];

  for (let i = 0; i < embaralhados.length; i += 2) {
    duplas.push([embaralhados[i], embaralhados[i + 1]]);
  }

  const duplasEmbaralhadas = shuffleArray(duplas);
  const partidas: RoundMatch[] = [];

  for (let i = 0; i < duplasEmbaralhadas.length; i += 2) {
    partidas.push({
      dupla1: duplasEmbaralhadas[i],
      dupla2: duplasEmbaralhadas[i + 1],
    });
  }

  return partidas;
}

function gerarRodadasComMistura(
  jogadores: string[],
  totalRodadas: number
): RoundMatch[][] {
  const teammateCounts = createCountMap(jogadores);
  const opponentCounts = createCountMap(jogadores);
  let lastRoundPartners = createCountMap(jogadores);
  let lastRoundOpponents = createCountMap(jogadores);
  const rodadas: RoundMatch[][] = [];

  for (let round = 0; round < totalRodadas; round++) {
    let bestRound: RoundMatch[] | null = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (let attempt = 0; attempt < 2500; attempt++) {
      const candidate = generateCandidateRound(jogadores);
      const score = computeRoundScore(
        candidate,
        teammateCounts,
        opponentCounts,
        lastRoundPartners,
        lastRoundOpponents
      );

      if (score < bestScore) {
        bestScore = score;
        bestRound = candidate;
      }
    }

    if (!bestRound) {
      throw new Error("Não foi possível gerar rodadas com boa mistura.");
    }

    rodadas.push(bestRound);
    registerRound(bestRound, teammateCounts, opponentCounts);

    const lastMaps = buildLastRoundMaps(jogadores, bestRound);
    lastRoundPartners = lastMaps.partners;
    lastRoundOpponents = lastMaps.opponents;
  }

  return rodadas;
}

function gerarRodadasSuper12(jogadores: string[]) {
  if (jogadores.length !== 12) {
    throw new Error("Jogadores devem ser 12 para super12.");
  }

  return gerarRodadasComMistura(jogadores, 11);
}

function gerarRodadasSuper8(jogadores: string[]) {
  if (jogadores.length !== 8) {
    throw new Error("Jogadores devem ser 8 para super8.");
  }

  return gerarRodadasComMistura(jogadores, 7);
}

export const iniciarTorneio = async () => {
  const snapshot = await get(torneioRef);
  if (!snapshot.exists()) {
    throw new Error("Torneio não encontrado");
  }

  const dados = snapshot.val();
  const inscritosObj = dados.inscritos || {};
  let jogadores = Object.keys(inscritosObj);

  const tipo = dados.tipo;

  if (tipo === "super12" && jogadores.length !== 12) {
    throw new Error("Torneio super12 precisa de exatamente 12 jogadores.");
  } else if (tipo === "super8" && jogadores.length !== 8) {
    throw new Error("Torneio super8 precisa de exatamente 8 jogadores.");
  }

  // Embaralha jogadores para variar as rodadas
  jogadores = shuffleArray(jogadores);

  let rodadas;
  if (tipo === "super12") {
    rodadas = gerarRodadasSuper12(jogadores);
  } else if (tipo === "super8") {
    rodadas = gerarRodadasSuper8(jogadores);
  } else {
    throw new Error("Tipo de torneio inválido.");
  }

  await update(torneioRef, {
    iniciado: true,
    iniciadoEm: Date.now(),
  });

  await set(child(torneioRef, "partidas"), rodadas);
};

export const salvarResultado = async (
  rodada: number,
  quadraIndex: number,
  dupla: "dupla1" | "dupla2",
  resultado: number
) => {
  const db = getDatabase();
  const caminhoResultado = `torneio/super/partidas/${rodada}/${quadraIndex}/${dupla}Resultado`;

  try {
    // Usar set para salvar o valor direto no caminho do resultado
    await set(ref(db, caminhoResultado), resultado);
  } catch (error) {
    console.error("Erro ao salvar resultado:", error);
    throw error;
  }
};

export const escutarResultados = (
  rodada: number,
  callback: (resultados: { placar1: number; placar2: number }[]) => void
) => {
  const db = getDatabase();
  const caminho = `torneio/super/partidas/${rodada}`;
  const resultadosRef = ref(db, caminho);

  return onValue(resultadosRef, (snapshot) => {
    if (snapshot.exists()) {
      const dados = snapshot.val();
      const resultados = Object.values(dados).map((quadra: any) => ({
        placar1: quadra.dupla1Resultado ?? -1,
        placar2: quadra.dupla2Resultado ?? -1,
      }));
      callback(resultados);
    } else {
      callback([]);
    }
  });
};

export const cancelarInscricao = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("Você precisa estar logado.");

  const userRef = ref(db, `torneio/super/inscritos/${user.uid}`);
  await remove(userRef);
};

export interface JogadorStats {
  userId: string;
  nome: string;
  vitorias: number;
  gamesFavor: number;
  saldoGames: number;
}

interface Partida {
  dupla1: string[];
  dupla2: string[];
  dupla1Resultado?: number | null;
  dupla2Resultado?: number | null;
}

type Callback = (classificacao: JogadorStats[], partidas: Partida[][]) => void;

export function getClassificacaoTorneio(callback: Callback) {
  const db = getDatabase();
  const torneioRef = ref(db, "torneio/super");

  const listener = onValue(torneioRef, (snapshot) => {
    const data = snapshot.val();
    if (!data) {
      callback([], []);
      return;
    }

    const inscritos = data.inscritos || {};
    const partidas: Partida[][] = data.partidas || [];

    const statsMap: Record<string, JogadorStats> = {};

    // Inicializa stats dos inscritos
    Object.entries(inscritos).forEach(([userId, info]: [string, any]) => {
      statsMap[userId] = {
        userId,
        nome: info.nome || "Sem nome",
        vitorias: 0,
        gamesFavor: 0,
        saldoGames: 0,
      };
    });

    // Processa partidas para atualizar stats
    partidas.forEach((rodada) => {
      rodada.forEach((partida) => {
        const {
          dupla1 = [],
          dupla2 = [],
          dupla1Resultado: res1,
          dupla2Resultado: res2,
        } = partida;

        // Ignora partidas sem resultado completo
        if (res1 == null || res2 == null) return;

        // Atualiza games a favor e saldo para cada jogador
        dupla1.forEach((userId) => {
          if (!statsMap[userId]) return;
          statsMap[userId].gamesFavor += res1;
          statsMap[userId].saldoGames += res1 - res2;
        });
        dupla2.forEach((userId) => {
          if (!statsMap[userId]) return;
          statsMap[userId].gamesFavor += res2;
          statsMap[userId].saldoGames += res2 - res1;
        });

        // Atualiza vitórias
        if (res1 > res2) {
          dupla1.forEach((userId) => {
            if (!statsMap[userId]) return;
            statsMap[userId].vitorias += 1;
          });
        } else if (res2 > res1) {
          dupla2.forEach((userId) => {
            if (!statsMap[userId]) return;
            statsMap[userId].vitorias += 1;
          });
        }
        // Empate não conta vitória
      });
    });

    // Ordena classificação
    const classificacao = Object.values(statsMap).sort((a, b) => {
      if (b.gamesFavor !== a.gamesFavor) return b.gamesFavor - a.gamesFavor;
      if (b.vitorias !== a.vitorias) return b.vitorias - a.vitorias;
      if (b.saldoGames !== a.saldoGames) return b.saldoGames - a.saldoGames;
      return a.nome.localeCompare(b.nome);
    });

    callback(classificacao, partidas);
  });

  return () => listener();
}

export const getNomesPorId = async (): Promise<Record<string, string>> => {
  const snapshot = await get(child(torneioRef, "inscritos"));
  const nomes: Record<string, string> = {};
  if (snapshot.exists()) {
    const data = snapshot.val();
    Object.entries(data).forEach(([uid, info]: [string, any]) => {
      nomes[uid] = info.nome || uid;
    });
  }
  return nomes;
};
